import { useEffect } from "react"
import { useForm } from "react-hook-form"

import isEqual from "lodash-es/isEqual"

import { useEvent } from "shared/lib/useEvent"
import {
  CreateDimConfigurationInput,
  DimConfiguration
} from "shared/types/__generated__/graphql"
import { DimConfigurationSchema } from "shared/types/__generated__/validation"

import { zodResolver } from "@hookform/resolvers/zod"
import I18n from "i18n"
import z from "zod"

import { DimModelCategory } from "../types/DimModelCategory"

export interface ConfigurationsFormData {
  configurations: DimConfiguration[]
}

export interface NewConfigurationFormData {
  productName: string
  productType: DimModelCategory | null
  dimensioningKey?: string
}

const newConfigurationSchema = z.object({
  productName: z.string().min(2),
  productType: z.enum(DimModelCategory, I18n.t("live:Validation.required")),
  dimensioningKey: z.string().optional()
})

interface UseDimConfigurationAdminPageViewModelProps {
  configurations: DimConfiguration[]
  deleteConfiguration: (id: string) => Promise<void>
  createConfiguration: (input: CreateDimConfigurationInput) => Promise<void>
  onChange: (input: DimConfiguration) => Promise<void>
}

export function useDimConfigurationAdminPageViewModel({
  configurations,
  deleteConfiguration,
  createConfiguration,
  onChange
}: UseDimConfigurationAdminPageViewModelProps) {
  // Form for existing configurations
  const configurationsForm = useForm<ConfigurationsFormData>({
    defaultValues: {
      configurations: configurations.map(config => ({
        id: config.id,
        productName: config.productName,
        productType: config.productType,
        dimensioningKey: config.dimensioningKey ?? "",
        isActive: config.isActive ?? false
      }))
    }
  })

  // Form for adding new configuration
  const newConfigurationForm = useForm<NewConfigurationFormData>({
    resolver: zodResolver<
      NewConfigurationFormData,
      unknown,
      z.infer<typeof newConfigurationSchema>
    >(newConfigurationSchema),
    defaultValues: {
      productName: "",
      productType: null,
      dimensioningKey: ""
    }
  })

  useEffect(() => {
    configurationsForm.reset({ configurations })
  }, [configurations])

  useEffect(() => {
    const subscription = configurationsForm.watch((value, { name }) => {
      if (!name?.startsWith("configurations.")) return

      // Extract index from field name (e.g., "configurations.0.isActive" -> 0)
      const indexMatch = name.match(/^configurations\.(\d+)/)
      if (!indexMatch) return

      const index = parseInt(indexMatch[1], 10)
      const changedConfig = value.configurations?.[index]
      const originalConfig = configurations[index]

      if (
        !changedConfig ||
        !originalConfig ||
        isEqual(changedConfig, originalConfig)
      )
        return

      // Validate the changed configuration
      const validationResult = z.safeParse(
        DimConfigurationSchema(),
        changedConfig
      )
      if (validationResult.success) {
        onChange(validationResult.data)
      }
    })
    return () => subscription.unsubscribe()
  }, [configurationsForm, configurations])

  const handleDelete = useEvent(async (index: number) => {
    const config = configurations[index]
    if (config) {
      await deleteConfiguration(config.id)
    }
  })

  const handleAddConfiguration = useEvent(
    newConfigurationForm.handleSubmit(async data => {
      if (!data.productType) return

      await createConfiguration({
        productName: data.productName,
        productType: data.productType,
        dimensioningKey: data.dimensioningKey || undefined,
        isActive: true
      })
      newConfigurationForm.reset({
        productName: "",
        productType: null,
        dimensioningKey: ""
      })
    })
  )

  return {
    configurationsForm,
    newConfigurationForm,
    handleDelete,
    handleAddConfiguration
  }
}
